import { cn } from "@app/ui";
import { useRef, type PointerEvent as ReactPointerEvent, type RefObject } from "react";
import { isSameYMD } from "./drag.js";
import type { DayCol } from "./build-days.js";
import { washStyle } from "../block-wash.js";
import { useBlockStyle } from "../useBlockStyle.js";
import { useTimeFormat } from "../../lib/useTimeFormat.js";
import { useCalendarsStore, useMobileSheetDraftStore, useSettingsStore, useThemeStore } from "../../stores/index.js";
import { resolvedCalendarColor } from "../entity-colors.js";

const SNAP_MIN = 15;
const DAY_MIN = 24 * 60;

interface Props {
  days: DayCol[];
  hourHeight: number;
  /** The wide day track (geometry for column / minute math). */
  trackRef: RefObject<HTMLDivElement | null>;
}

type DragKind = "move" | "top" | "bottom";

/**
 * The pending create-from-calendar entry, shown as a live draggable ghost on
 * the timed grid while the mobile create sheet is open. Circular handles on
 * top/bottom drag the start/end; pressing the middle slides the whole block
 * (across days too). Reads/writes the mobileSheetDraft bridge store, which the
 * sheet keeps in sync with its form schedule.
 *
 * The handles use pointer capture so they keep receiving moves after the finger
 * leaves the small circles.
 */
export function MobileDraftGhost({ days, hourHeight, trackRef }: Props) {
  const { active, day, startMin, durationMin, title, isEvent } = useMobileSheetDraftStore();
  const tf = useTimeFormat();
  const { tint, gradient } = useBlockStyle();
  const calendars = useCalendarsStore((s) => s.calendars);
  const defaultCalId = useSettingsStore((s) => s.defaultCalendarId);
  const theme = useThemeStore((s) => s.config);
  const geo = useRef({ hourHeight, days });
  geo.current = { hourHeight, days };

  if (!active || !day) return null;
  const col = days.findIndex((d) => isSameYMD(d.date, day));
  if (col < 0) return null;
  const n = days.length;

  const defaultCalColor = resolvedCalendarColor(theme, calendars, defaultCalId ?? calendars[0]?.id ?? null);
  const wash = washStyle({ priority: "none", custom_color: null, is_event: isEvent }, defaultCalColor, tint, gradient);
  const ghostHeight = (Math.max(durationMin, SNAP_MIN) / 60) * hourHeight;

  function minuteAt(clientY: number): number {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect) return 0;
    return ((clientY - rect.top) / geo.current.hourHeight) * 60;
  }
  function colAt(clientX: number): number {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect) return col;
    return Math.max(0, Math.min(geo.current.days.length - 1, Math.floor(((clientX - rect.left) / rect.width) * geo.current.days.length)));
  }

  // Pointer events + setPointerCapture: the small circular handles keep
  // receiving move/up even once the finger slides off them (React synthetic
  // touch handlers proved unreliable here, and capture is the robust fix —
  // `touch-none` on the targets stops the page from scrolling under the drag).
  function startDrag(kind: DragKind) {
    return (e: ReactPointerEvent) => {
      e.stopPropagation();
      const el = e.currentTarget as HTMLElement;
      try { el.setPointerCapture(e.pointerId); } catch { /* unsupported */ }
      const s0 = useMobileSheetDraftStore.getState();
      const grabOffsetMin = minuteAt(e.clientY) - s0.startMin;
      const endMin = s0.startMin + s0.durationMin;

      const onMove = (ev: PointerEvent) => {
        const { setTimes, setDay, startMin: sMin, durationMin: dMin, day: curDay } = useMobileSheetDraftStore.getState();
        const raw = minuteAt(ev.clientY);
        if (kind === "move") {
          const top = Math.max(0, Math.min(DAY_MIN - dMin, raw - grabOffsetMin));
          setTimes(top, dMin);
          const target = geo.current.days[colAt(ev.clientX)];
          if (target && !isSameYMD(target.date, curDay ?? new Date())) setDay(new Date(target.date));
        } else if (kind === "top") {
          const newStart = Math.max(0, Math.min(endMin - SNAP_MIN, raw));
          setTimes(newStart, endMin - newStart);
        } else {
          setTimes(sMin, Math.max(SNAP_MIN, raw - sMin));
        }
      };
      const onEnd = () => {
        el.removeEventListener("pointermove", onMove);
        el.removeEventListener("pointerup", onEnd);
        el.removeEventListener("pointercancel", onEnd);
        try { el.releasePointerCapture(e.pointerId); } catch { /* already released */ }
      };
      el.addEventListener("pointermove", onMove);
      el.addEventListener("pointerup", onEnd);
      el.addEventListener("pointercancel", onEnd);
    };
  }

  const handle = "pointer-events-auto absolute z-10 h-5 w-5 touch-none rounded-full border-2 border-primary bg-background/50 shadow-md";

  return (
    <div
      data-quick-ghost
      className="pointer-events-none absolute z-40"
      style={{
        left: `${(col / n) * 100}%`,
        width: `${100 / n}%`,
        top: (startMin / 60) * hourHeight,
        height: ghostHeight,
      }}
    >
      <div
        className={cn(
          "pointer-events-auto absolute inset-y-0 left-0.5 right-1 touch-none overflow-hidden rounded border-2 border-dashed border-primary px-1.5 py-0.5 shadow-lg",
          wash.washed && "task-wash",
        )}
        style={{ ...wash.vars, position: "absolute", top: 0, bottom: 0, minHeight: 0 }}
        onPointerDown={startDrag("move")}
      >
        <div className="break-words text-xs font-medium leading-tight">
          {title.trim() || (isEvent ? "New event" : "New task")}
        </div>
        <div className="break-words font-mono text-[calc(10px*var(--app-text-scale))] leading-tight opacity-80">
          {tf.range(startMin, startMin + durationMin)}
        </div>
      </div>
      <div className={cn(handle, "left-0.5 top-0 -translate-x-1/2 -translate-y-1/2")} onPointerDown={startDrag("top")} aria-hidden />
      <div className={cn(handle, "bottom-0 right-1 translate-x-1/2 translate-y-1/2")} onPointerDown={startDrag("bottom")} aria-hidden />
    </div>
  );
}
